import type { ImportMessage, ImportQuestion, SendImportMessageBody } from '@ni/shared';
import { isApiError } from '../../api/errors';

/**
 * **Which of the planner's questions are still open, and what the transcript
 * already says about the rest** (import note §6.3).
 *
 * The chat used to decide this with one rule — *the questions on the newest
 * agent message* — and never read the answers that followed them. So a refresh
 * put every option back under a question that had already been settled, and the
 * same question could be answered twice, each answer costing a turn.
 *
 * **Answered is a fact the transcript holds**, and the rule is a comparison of
 * `seq`: a question is answered when a **user message later than the agent
 * message that asked it** carries an answer for its id. Later by `seq`, not
 * "anywhere on the session", because the planner deliberately re-asks by the
 * same id — the stub re-asks `level` — and an answer to the first asking must
 * not settle the second. The server reads the same rule under its own lock and
 * refuses a repeat with `409 IMPORT_QUESTION_ANSWERED`, so this is the page
 * showing what the server will say rather than a guess beside it.
 *
 * Everything here is a function over plain messages, out of the component, so
 * the suite that has no DOM can hold all of it.
 */

/** One of the questions on the newest agent turn, and what settled it if anything did. */
export interface StandingQuestion {
  question: ImportQuestion;
  /**
   * The answer already on the transcript, in the office's own words, or `null`
   * while the question is still open. `{ text }` rather than a bare string so a
   * later key — who answered it, when — is a widening and not a rewrite.
   */
  answered: { text: string } | null;
}

/** The agent message with the greatest `seq`, which is the turn still being answered. */
function newestAgentMessage(messages: readonly ImportMessage[]): ImportMessage | null {
  let newest: ImportMessage | null = null;
  for (const message of messages) {
    if (message.role !== 'agent') continue;
    if (newest === null || message.seq >= newest.seq) newest = message;
  }
  return newest;
}

/**
 * **The standing questions**: the newest agent turn's, each with the answer the
 * transcript holds for it.
 *
 * An older turn's questions were either answered or superseded by the turn that
 * followed, and offering them again would be asking the office to settle
 * something the planner has stopped asking about.
 *
 * Forgiving of the rows written before any of these keys existed: a message with
 * no `questions` asks nothing, and a message with no `answers` settles nothing.
 */
export function standingQuestions(messages: readonly ImportMessage[]): StandingQuestion[] {
  const newest = newestAgentMessage(messages);
  const questions = newest?.questions ?? [];
  if (newest === null || questions.length === 0) return [];

  return questions.map((question) => ({
    question,
    answered: latestAnswer(messages, question.id, newest.seq),
  }));
}

/**
 * The last thing said about one question *after* it was asked — latest by
 * `seq`, and within a single message the last of its answers, because a body
 * that names a question twice means the second one.
 */
function latestAnswer(
  messages: readonly ImportMessage[],
  questionId: string,
  askedAt: number,
): { text: string } | null {
  let found: { seq: number; text: string } | null = null;
  for (const message of messages) {
    if (message.role !== 'user' || message.seq <= askedAt) continue;
    for (const answer of message.answers ?? []) {
      if (answer.question_id !== questionId) continue;
      if (found === null || message.seq >= found.seq) {
        found = { seq: message.seq, text: answer.text };
      }
    }
  }
  return found === null ? null : { text: found.text };
}

/** What one of the office's own turns settled: the question, and the answer to it. */
export interface AnsweredLine {
  question: string;
  answer: string;
}

/**
 * **What a user message answered, read back as sentences** rather than as a
 * count.
 *
 * "1 question answered" was the whole of what the transcript said about the
 * turn that mattered most — the office pressed an option and then could not see
 * which question they had pressed it under. The question's own text comes off
 * the nearest **earlier** agent message that asked the id: nearest, because the
 * planner re-asks by the same id, and the asking this answer belongs to is the
 * last one before it.
 *
 * An answer naming a question no turn asked is kept and printed under its id —
 * the server keeps those deliberately, so the page must be able to show one.
 */
export function answeredLines(
  messages: readonly ImportMessage[],
  message: ImportMessage,
): AnsweredLine[] {
  return (message.answers ?? []).map((answer) => ({
    question: questionTextBefore(messages, answer.question_id, message.seq) ?? answer.question_id,
    answer: answer.text,
  }));
}

function questionTextBefore(
  messages: readonly ImportMessage[],
  questionId: string,
  before: number,
): string | null {
  let found: { seq: number; text: string } | null = null;
  for (const message of messages) {
    if (message.role !== 'agent' || message.seq >= before) continue;
    for (const question of message.questions ?? []) {
      if (question.id !== questionId) continue;
      if (found === null || message.seq >= found.seq) {
        found = { seq: message.seq, text: question.text };
      }
    }
  }
  return found === null ? null : found.text;
}

/**
 * **A pick the office has made and not sent yet** — the option's words, and
 * nothing else.
 *
 * It used to carry a `change` flag frozen at the moment the option was pressed,
 * and a frozen flag is a reading of the record that goes stale while the batch
 * sits here: pick under an open question, press Send, have the planner 502, and
 * the row the server wrote before the model call now settles that very question
 * — but the pick still says `change: false`, so Send offers a body the server
 * can only refuse. Whether a pick is a repeat is a fact about the record, so it
 * is read off the record at Send. See {@link buildMessageBody}.
 */
export interface PendingPick {
  text: string;
}

/**
 * **One pick recorded against the question it settles**, out of the component so
 * the toggle is a thing a spec can hold.
 *
 * One pick per question, switched in place. Pressing the option that is already
 * picked takes it back off the batch — the only way out of a pick that does not
 * involve sending one, and cheap, because nothing has been sent.
 */
export function buildPicks(
  picks: ReadonlyMap<string, PendingPick>,
  questionId: string,
  text: string,
): ReadonlyMap<string, PendingPick> {
  const next = new Map(picks);
  if (next.get(questionId)?.text === text) next.delete(questionId);
  else next.set(questionId, { text });
  return next;
}

/**
 * **The whole batch as one body** (§6.3).
 *
 * Pressing an option used to send a turn. Now the picks are held in the tab
 * until Send, and this is what Send sends: the composer's text if there is any,
 * every pick under `answers`, and the ids of the deliberate repeats under
 * `changes`. `text` may be empty when there are answers — a sentence nobody
 * typed would reach the model as something the office had just said.
 *
 * **`changes` is read here, at Send, and not when the option was pressed.** It
 * names the picks the record already holds an answer for — `standing`, which is
 * {@link standingQuestions} over the transcript as the page last saw it — and
 * that is a fact that moves while a batch waits: a turn that failed at the model
 * had already written the office's row, so a question that was open when the
 * option was pressed is settled by the time Send is pressed again. A flag frozen
 * at the press would say "not a change" about an answer the server can now only
 * refuse, and the office would be holding a Send that cannot work.
 *
 * `null` is "there is nothing to send", which is also what disables the button:
 * one answer to the question rather than a rule here and a different one on the
 * button.
 */
export function buildMessageBody(
  text: string,
  picks: ReadonlyMap<string, PendingPick>,
  standing: readonly StandingQuestion[] = [],
): SendImportMessageBody | null {
  const typed = text.trim();
  const answers = [...picks].map(([question_id, pick]) => ({ question_id, text: pick.text }));
  const settled = new Set(
    standing.filter((one) => one.answered !== null).map((one) => one.question.id),
  );
  const changes = [...picks.keys()].filter((questionId) => settled.has(questionId));
  if (typed.length === 0 && answers.length === 0) return null;
  return {
    text: typed,
    ...(answers.length > 0 ? { answers } : {}),
    ...(changes.length > 0 ? { changes } : {}),
  };
}

/**
 * **The same turn again, after the planner could not be reached** (§6.3).
 *
 * `502 IMPORT_PLANNER_UNAVAILABLE` says nobody answered — but the office's own
 * message was written, with its answers on it, *before* the model was called and
 * stays written whatever the model does. So the transcript now holds an answer
 * for every question the failed body named, and sending that body back
 * unchanged walks into `409 IMPORT_QUESTION_ANSWERED`: the Retry button
 * disappears with the 409, and the session is stuck with a turn nobody can
 * finish.
 *
 * What the retry means is "this is the turn that failed, again" — and that is
 * `retry`, not `changes`. Putting every answered id under `changes` was the
 * first fix and it says too much: `changes` means *replace what is there*, with
 * no word about what was there, so a retry pressed in one tab after a second tab
 * had changed the same answer would be accepted and would revert it. `retry`
 * says the narrow thing instead — take these answers where they are exactly what
 * the record already holds, and refuse the id where a later message moved it.
 *
 * Pure, and the body it is given is the body that failed: the composer has moved
 * on by then, which is the bug this whole door exists to keep out.
 */
export function retryBody(failed: SendImportMessageBody): SendImportMessageBody {
  const { changes: _dropped, ...rest } = failed;
  return { ...rest, retry: true };
}

/**
 * The ids the server named when it refused the batch — the questions it already
 * holds an answer for, which is `ImportQuestionAnsweredDetails` on the wire.
 * Read defensively rather than parsed: the page drops those picks and keeps
 * the rest, and a refusal whose details are not the shape expected must still
 * leave the office holding what they picked.
 */
export function answeredQuestionIds(error: unknown): string[] {
  if (!isApiError(error)) return [];
  const ids = error.details?.['question_ids'];
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is string => typeof id === 'string');
}

/**
 * **The turn is in flight and this tab is not the one that sent it** (§6.3) — a
 * refresh mid-turn loses the mutation, and what is left is a transcript ending
 * in the office's own message with no reply under it.
 *
 * So the marker hangs off the transcript's own shape: the last message, when it
 * is a user message, is the one waiting. The clock runs from its `at`.
 */
export function messageAwaitingReply(messages: readonly ImportMessage[]): ImportMessage | null {
  const last = messages[messages.length - 1];
  return last && last.role === 'user' ? last : null;
}
